/* Fresh Prints music: explicit listening preferences, never a page-click autoplay trap. */
(() => {
  'use strict';

  const STORAGE_KEY = 'fpMusicPreferences:v1';
  const DAY = 24 * 60 * 60 * 1000;
  const DEFAULTS = Object.freeze({ version: 1, autoplay: false, paused: true, volume: 20, snoozeUntil: 0 });
  const PLAY_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>';
  const PAUSE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7 5h4v14H7zm6 0h4v14h-4z"/></svg>';

  function readPreferences(raw) {
    try {
      const value = JSON.parse(raw);
      if (!value || value.version !== 1 || typeof value.autoplay !== 'boolean' || typeof value.paused !== 'boolean') return { ...DEFAULTS };
      return {
        ...DEFAULTS,
        autoplay: value.autoplay,
        paused: value.paused,
        volume: typeof value.volume === 'number' && Number.isFinite(value.volume) ? Math.min(100, Math.max(0, value.volume)) : DEFAULTS.volume,
        snoozeUntil: typeof value.snoozeUntil === 'number' && Number.isFinite(value.snoozeUntil) && value.snoozeUntil > 0 ? value.snoozeUntil : 0,
      };
    } catch (_) { return { ...DEFAULTS }; }
  }

  function installDockOffset(root, win) {
    const doc = root.ownerDocument || win.document;
    if (!doc?.querySelectorAll || !win.requestAnimationFrame || !root.style) return () => {};
    const selector = '.fp-sticker-studio .purchase-bar, .fgs-purchase, .bbs .bb__purchase-bar';
    let frame = null;
    let stopped = false;
    let lastOffset = 0;
    const observed = new Set();
    const measure = () => {
      frame = null;
      if (stopped) return;
      const height = win.innerHeight || doc.documentElement.clientHeight;
      let bottom = 88;
      doc.querySelectorAll(selector).forEach((bar) => {
        if (observer && !observed.has(bar)) { observed.add(bar); observer.observe(bar); }
        if (!bar.getClientRects().length) return;
        const bounds = bar.getBoundingClientRect();
        if (bounds.height > 0 && bounds.top >= 0 && bounds.top < height && bounds.bottom >= height - 2) bottom = Math.max(bottom, Math.ceil(height - bounds.top + 12));
      });
      if (bottom !== lastOffset) { lastOffset = bottom; root.style.setProperty('--fpm-dock-bottom', `${bottom}px`); }
    };
    const queue = () => { if (!stopped && frame === null) frame = win.requestAnimationFrame(measure); };
    const observer = win.ResizeObserver ? new win.ResizeObserver(queue) : null;
    const watched = [[win, 'resize'], [win, 'scroll'], [win, 'load'], [doc, 'focusin'], [doc, 'focusout']];
    watched.forEach(([target, name]) => target.addEventListener(name, queue, { passive: true }));
    queue();
    return () => {
      stopped = true;
      if (frame !== null) win.cancelAnimationFrame(frame);
      observer?.disconnect();
      watched.forEach(([target, name]) => target.removeEventListener(name, queue));
    };
  }

  function createMusicPlayer(root, environment = {}) {
    if (!root || root.dataset.musicReady === 'true') return null;
    const win = environment.window || window;
    const audio = root.querySelector('[data-music-audio]');
    if (!audio) return null;
    root.dataset.musicReady = 'true';
    const dialog = root.querySelector('[data-music-dialog]');
    const volume = root.querySelector('[data-music-volume]');
    const autoplay = root.querySelector('[data-music-autoplay]');
    const now = environment.now || Date.now;
    const schedule = environment.setTimeout || win.setTimeout.bind(win);
    const unschedule = environment.clearTimeout || win.clearTimeout.bind(win);
    const listeners = [];
    let storage;
    try { storage = environment.storage || win.localStorage; } catch (_) { storage = null; }
    let preferences;
    try { preferences = readPreferences(storage?.getItem(STORAGE_KEY)); } catch (_) { preferences = { ...DEFAULTS }; }
    let generation = 0;
    let wantsPlayback = false;
    let loading = false;
    let timer = null;
    let error = '';
    let notice = '';
    let opener = null;
    let destroyed = false;
    let pageHidden = false;
    let deviceVolumeOnly = false;
    const configured = !!(audio.getAttribute('src') || audio.querySelector('source[src]'));

    const on = (target, name, handler) => {
      if (!target) return;
      target.addEventListener(name, handler);
      listeners.push(() => target.removeEventListener(name, handler));
    };
    const snoozed = () => preferences.snoozeUntil > now();
    const save = () => { try { storage?.setItem(STORAGE_KEY, JSON.stringify(preferences)); } catch (_) { /* Listening still works when storage is blocked. */ } };
    const clearTimer = () => { if (timer !== null) unschedule(timer); timer = null; };
    const text = (selector, value) => root.querySelectorAll(selector).forEach((node) => { if (node.textContent !== value) node.textContent = value; });
    function applyVolume() {
      try { audio.volume = preferences.volume / 100; } catch (_) { /* Some phones require hardware volume controls. */ }
      deviceVolumeOnly = Math.abs(audio.volume - preferences.volume / 100) > 0.015;
    }

    function render() {
      if (destroyed) return;
      const playing = wantsPlayback && !audio.paused && !audio.ended;
      const active = wantsPlayback && (loading || playing);
      const state = error ? 'error' : loading ? 'loading' : playing ? 'playing' : snoozed() ? 'snoozed' : preferences.paused ? 'off' : 'paused';
      root.dataset.musicState = state;
      root.dataset.musicPlaying = String(playing && !loading);
      root.querySelectorAll('[data-music-toggle]').forEach((button) => {
        button.disabled = !configured;
        button.setAttribute('aria-label', active ? 'Pause Hot Z95' : 'Play Hot Z95');
        button.setAttribute('aria-pressed', String(playing));
        const icon = button.querySelector('[data-music-toggle-icon]');
        if (icon) icon.innerHTML = active ? PAUSE_ICON : PLAY_ICON;
        const label = button.querySelector('[data-music-toggle-label]');
        if (label) label.textContent = active ? 'Pause' : 'Play Hot Z95';
      });
      if (volume) {
        volume.value = String(Math.round(audio.volume * 100));
        volume.disabled = deviceVolumeOnly;
        volume.setAttribute('aria-valuetext', deviceVolumeOnly ? 'Use your device volume controls' : `${Math.round(audio.volume * 100)} percent`);
        volume.setAttribute('title', deviceVolumeOnly ? 'Use your device volume buttons' : 'Music volume');
      }
      if (autoplay) autoplay.checked = preferences.autoplay;
      text('[data-music-volume-label]', deviceVolumeOnly ? 'Device' : `${Math.round(audio.volume * 100)}%`);
      text('[data-music-pill-status]', error ? 'Tap to retry' : loading ? 'Connecting…' : playing ? audio.volume === 0 ? 'Muted' : 'Hot Z95 · Live' : snoozed() ? 'Paused 24h' : 'Sound off');
      text('[data-music-status]', !configured ? 'The radio stream is unavailable right now.' : error || notice || (loading ? 'Connecting to Hot Z95… You can pause at any time.' : playing ? 'Hot Z95 is playing.' : snoozed() ? 'Website music is paused for 24 hours. Press Play to listen sooner.' : 'Sound is off. Press Play whenever you feel like listening.'));
      updateMediaState(playing && !loading);
    }

    function updateMediaState(playing) {
      try {
        if (win.navigator?.mediaSession && mediaOwned) win.navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
      } catch (_) { /* Browser media controls are an optional enhancement. */ }
    }

    let mediaOwned = false;
    function registerMediaControls() {
      try {
        const session = win.navigator?.mediaSession;
        if (!session || mediaOwned) return;
        mediaOwned = true;
        if (win.MediaMetadata) session.metadata = new win.MediaMetadata({ title: 'Hot Z95', artist: 'Fresh Prints · Live radio' });
        session.setActionHandler('play', () => play(false));
        session.setActionHandler('pause', () => pause('pause'));
        session.setActionHandler('stop', () => pause('off'));
      } catch (_) { /* Some browsers expose only part of Media Session. */ }
    }

    function pause(kind = 'pause') {
      generation += 1;
      wantsPlayback = false;
      loading = false;
      error = '';
      clearTimer();
      if (kind === 'snooze') {
        preferences.snoozeUntil = now() + DAY;
        // Retain the prior arrival preference; expiry never starts sound on this page.
        notice = '';
      } else {
        preferences.paused = true;
        preferences.snoozeUntil = 0;
        if (kind !== 'pause') preferences.autoplay = false;
        notice = kind === 'own' ? 'Website music is off. Keep listening in your own music app.' : kind === 'spotify' ? 'Website music is off. Spotify opens separately for your music.' : kind === 'off' ? 'Website music will stay off until you choose Play.' : '';
      }
      save();
      audio.pause();
      render();
    }

    function fail(message) {
      generation += 1;
      wantsPlayback = false;
      loading = false;
      clearTimer();
      audio.pause();
      error = message;
      notice = '';
      render();
    }

    function watchConnection() {
      if (timer !== null) return;
      const attempt = generation;
      timer = schedule(() => {
        if (attempt !== generation || !wantsPlayback) return;
        fail('Hot Z95 is taking too long to connect. Press Play to try again.');
      }, environment.connectionTimeout || 15000);
    }

    async function play(automatic = false) {
      if (destroyed || pageHidden || !configured) return;
      if (automatic && (!preferences.autoplay || preferences.paused || snoozed())) return;
      if (wantsPlayback && (loading || (!audio.paused && !audio.ended))) return;
      generation += 1;
      clearTimer();
      const attempt = generation;
      if (!automatic) {
        preferences.autoplay = true;
        preferences.paused = false;
        preferences.snoozeUntil = 0;
        save();
      }
      wantsPlayback = true;
      loading = true;
      error = '';
      notice = '';
      applyVolume();
      registerMediaControls();
      render();
      watchConnection();
      try {
        if (audio.error && typeof audio.load === 'function') audio.load();
        await audio.play();
        if (destroyed || attempt !== generation) {
          if (!wantsPlayback) audio.pause();
          return;
        }
        // The 'playing' event owns the live state and cancels the watchdog.
        // A resolved request by itself must not paint a buffered stream as live.
        render();
      } catch (cause) {
        if (destroyed || attempt !== generation) return;
        fail(cause?.name === 'NotAllowedError' ? 'Your browser needs you to press Play to start the music.' : 'Hot Z95 could not connect. Press Play to try again.');
      }
    }

    function openDialog(event) {
      if (!dialog) return;
      opener = event?.currentTarget || root.querySelector('[data-music-open]');
      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      }
      root.querySelectorAll('[data-music-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
      dialog.querySelector('[data-music-close]')?.focus();
    }
    function closeDialog() {
      if (!dialog) return;
      if (typeof dialog.close === 'function') dialog.close();
      else { dialog.removeAttribute('open'); restoreFocus(); }
    }
    function restoreFocus() {
      root.querySelectorAll('[data-music-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      if (opener?.isConnected !== false) opener?.focus({ preventScroll: true });
    }

    root.querySelectorAll('[data-music-open]').forEach((button) => on(button, 'click', openDialog));
    root.querySelectorAll('[data-music-close]').forEach((button) => on(button, 'click', closeDialog));
    root.querySelectorAll('[data-music-toggle]').forEach((button) => on(button, 'click', (event) => {
      event.stopPropagation();
      if (wantsPlayback && (loading || !audio.paused)) pause();
      else play(false);
    }));
    on(dialog, 'close', restoreFocus);
    on(dialog, 'cancel', (event) => { event.preventDefault(); closeDialog(); });
    on(dialog, 'click', (event) => {
      if (event.target !== dialog || typeof dialog.getBoundingClientRect !== 'function') return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDialog();
    });
    on(volume, 'input', () => {
      const next = Number(volume.value);
      if (!Number.isFinite(next)) return;
      preferences.volume = Math.max(0, Math.min(100, next));
      applyVolume();
      save();
      render();
    });
    on(autoplay, 'change', () => {
      preferences.autoplay = autoplay.checked;
      if (preferences.autoplay) {
        preferences.paused = false;
        preferences.snoozeUntil = 0;
      }
      save();
      notice = preferences.autoplay ? 'Music is enabled across pages in this browser. You can still pause any time.' : 'Page-to-page playback is off. Press Play to enable it again.';
      render();
    });
    on(root.querySelector('[data-music-snooze]'), 'click', () => pause('snooze'));
    on(root.querySelector('[data-music-off]'), 'click', () => pause('off'));
    on(root.querySelector('[data-music-own]'), 'click', () => pause('own'));
    on(root.querySelector('[data-music-spotify]'), 'click', () => pause('spotify'));

    on(audio, 'playing', () => {
      if (!wantsPlayback || destroyed) { audio.pause(); return; }
      loading = false;
      clearTimer();
      error = '';
      notice = '';
      render();
    });
    on(audio, 'play', () => {
      if (!wantsPlayback || destroyed) { audio.pause(); return; }
      render();
    });
    on(audio, 'pause', () => {
      if (wantsPlayback) {
        generation += 1;
        wantsPlayback = false;
        loading = false;
        clearTimer();
        // Browser interruptions are not a deliberate Pause. Only the controls
        // above save a stop preference, so navigation retains listening intent.
      }
      render();
    });
    ['waiting', 'stalled'].forEach((name) => on(audio, name, () => {
      if (!wantsPlayback) return;
      loading = true;
      render();
      watchConnection();
    }));
    on(audio, 'ended', () => { if (wantsPlayback) pause(); });
    on(audio, 'error', () => { if (wantsPlayback) fail('Hot Z95 is unavailable right now. Press Play to retry.'); });
    on(win, 'storage', (event) => {
      if (event.key !== STORAGE_KEY && event.key !== null) return;
      preferences = readPreferences(event.newValue);
      applyVolume();
      if (preferences.paused || snoozed()) {
        generation += 1;
        wantsPlayback = false;
        loading = false;
        error = '';
        notice = '';
        clearTimer();
        audio.pause();
      }
      // Other tabs may stop this player, but never start it unexpectedly.
      render();
    });
    on(win, 'pagehide', () => {
      pageHidden = true;
      generation += 1;
      wantsPlayback = false;
      loading = false;
      clearTimer();
      audio.pause();
    });
    on(win, 'pageshow', (event) => {
      pageHidden = false;
      if (!event.persisted || destroyed) return;
      // A cached page may have missed preference changes made elsewhere.
      try { if (storage) preferences = readPreferences(storage.getItem(STORAGE_KEY)); } catch (_) { /* Retain in-memory preferences when storage is blocked. */ }
      applyVolume();
      error = '';
      notice = '';
      render();
      play(true);
    });

    applyVolume();
    audio.autoplay = false;
    const stopDock = installDockOffset(root, win);
    render();
    if (preferences.autoplay && !preferences.paused && !snoozed()) play(true);

    return {
      play: () => play(false),
      pause,
      getPreferences: () => ({ ...preferences }),
      destroy() {
        destroyed = true;
        generation += 1;
        wantsPlayback = false;
        clearTimer();
        stopDock();
        listeners.forEach((remove) => remove());
        audio.pause();
        if (mediaOwned) {
          try {
            ['play', 'pause', 'stop'].forEach((name) => win.navigator.mediaSession.setActionHandler(name, null));
            win.navigator.mediaSession.playbackState = 'none';
          } catch (_) { /* Optional browser API. */ }
        }
        delete root.dataset.musicReady;
      },
    };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { createMusicPlayer, readPreferences, installDockOffset, STORAGE_KEY };
  if (typeof document !== 'undefined') {
    const initialize = () => createMusicPlayer(document.getElementById('fp-music-player'));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
    else initialize();
  }
})();
